// ni-autocomplete.component.ts
import { 
  Component, Input, Output, EventEmitter, ViewChild, ElementRef, 
  HostListener, ViewChildren, QueryList, forwardRef, SimpleChanges, 
  OnChanges, ChangeDetectorRef, OnInit, OnDestroy, AfterViewInit, AfterViewChecked,
  ChangeDetectionStrategy
} from "@angular/core";
import { NgIf, NgFor, NgClass, CommonModule } from "@angular/common";
import { FormsModule, NG_VALUE_ACCESSOR, ControlValueAccessor } from "@angular/forms";
import { animate, style, transition, trigger, query, sequence, stagger, state } from "@angular/animations";
import { Subject, Observable, of } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { TooltipDirective } from "../ni-tooltip/ni-tooltip.directive";
import { NiIconComponent } from "../ni-icon/ni-icon.component";
import { NiOptionComponent } from "../ni-option/ni-option.component";

// Import interfaces and utilities from separate files
import { 
  AutocompleteOption, 
  AutocompleteGroup
} from './models/ni-autocomplete.model';

// Import services and directives
import { NiAutocompleteService } from './services/ni-autocomplete.service';

// Additional interfaces specific to this component
export interface AutocompleteApiResponse {
  data: AutocompleteOption[];
  totalItems?: number;
  total?: number;
  count?: number;
  length?: number;
}

export interface AutocompleteLazyLoadEvent {
  first: number;
  last: number;
  query?: string;
}

export interface AutocompleteCompleteEvent {
  query: string;
  originalEvent?: Event;
}

export interface AutocompleteSelectEvent {
  originalEvent: Event;
  value: AutocompleteOption;
}

export interface AutocompleteUnselectEvent {
  originalEvent: Event;
  value: AutocompleteOption;
}

@Component({
  selector: "NiAutocomplete",
  templateUrl: "./ni-autocomplete.component.html",
  styleUrls: ["./ni-autocomplete.component.scss"],
  standalone: true,
  imports: [
    NgIf, NiIconComponent, NgFor, NgClass, FormsModule, 
    TooltipDirective, CommonModule, NiOptionComponent
  ],
  providers: [{ 
    provide: NG_VALUE_ACCESSOR, 
    useExisting: forwardRef(() => NiAutocompleteComponent), 
    multi: true 
  }],
  animations: [
    trigger('dropDownMenu', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-10px)' }),
        query('.menu-item', [
          style({ opacity: 0, transform: 'translateY(-20px)' })
        ]),
        sequence([
          animate('100ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
          query('.menu-item', [
            stagger(30, [
              animate('100ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
            ])
          ])
        ])
      ]),
     
    ]),
  
    trigger('floatLabelAnimation', [
      transition(':enter', [
        style({ transform: 'translateY(0) scale(1)', opacity: 0 }),
        animate('200ms ease-out', style({ transform: 'translateY(-8px) scale(0.85)', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ transform: 'translateY(0) scale(1)', opacity: 0 }))
      ])
    ]),
  
    trigger('contentExpansion', [
      state('expanded', style({ height: '*', opacity: 1, visibility: 'visible' })),
      state('collapsed', style({ height: '0px', opacity: 0, visibility: 'hidden' })),
      transition('expanded <=> collapsed',
        animate('200ms cubic-bezier(.37,1.04,.68,.98)'))
    ])
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NiAutocompleteComponent implements OnChanges, OnInit, OnDestroy, ControlValueAccessor, AfterViewInit, AfterViewChecked {
  // Input Properties

  // --- Core Inputs ---
  @Input() label: string = "";
  @Input() selected: string | number | Array<string | number> | AutocompleteOption | Array<AutocompleteOption> | null = null;
  @Input() options: Array<AutocompleteOption> = [];
  @Input() valueField: string = "value";
  @Input() stateField: string = "name";
  @Input() optionLabel: string = "name";
  @Input() showSubtextInSelection: boolean = false;
  @Input() selectedSubtextSeparator: string = " - ";
  @Input() placeholder: string = "Select...";
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() hasError: boolean = false;
  @Input() errorMessage: string = "";
  @Input() border: boolean = true;
  @Input() multiple: boolean = false;
  @Input() showDropdownIcon: boolean = true;
  @Input() showClearButton: boolean = false;
  @Input() clearIcon: string = "x_rounded";
  @Input() clearIconClass: string = "w-5 h-5 text-white";
  @Input() dropdownIcon: string = "arrow_down";
  @Input() checkIcon: string = "tick";
  @Input() leadingIcon: string = "";
  @Input() trailingIcon: string = "";
  @Input() leadingIconClass: string = "w-5 h-5";
  @Input() trailingIconClass: string = "w-5 h-5";
  @Input() minLength: number = 100;
  @Input() minCharsToOpen: number = 3; // Minimum characters to type before opening options panel
  @Input() delay: number = 300;
  @Input() apiUrl: string = "";
  @Input() apiParams: any = {};
  @Input() pageSize: number = 20;
  @Input() scrollThreshold: number = 0.8;
  @Input() emptyMessage: string = "No data found";
  @Input() searchMessage: string = "Searching...";
  @Input() dropdown: boolean = false;
  @Input() dropdownMode: 'blank' | 'current' = 'blank';
  @Input() forceSelection: boolean = false;
  @Input() group: boolean = false;
  @Input() groupField: string = "group";
  @Input() typeahead: boolean = false;
  @Input() size: 'small' | 'normal' | 'large' = 'large';
  @Input() variant: 'outlined' | 'filled' = 'outlined';
  @Input() fluid: boolean = false;
  @Input() inputId: string = "";
  @Input() ariaLabel: string = "";
  @Input() ariaLabelledBy: string = "";
  @Input() ariaDescribedBy: string = "";
  @Input() autoZIndex: boolean = true;
  @Input() baseZIndex: number = 1000;
  @Input() appendTo: string = "body";
  @Input() autoHighlight: boolean = false;
  @Input() highlightOnFocus: boolean = true;
  @Input() maxLength: number = 100;
  @Input() inputPattern: string = ""; // Regex pattern for input validation
  @Input() inputType: 'text' | 'number' | 'email' | 'tel' | 'url' = 'text'; // Input type for validation
  @Input() allowOnlyNumbers: boolean = false; // Allow only numeric input
  @Input() allowOnlyLetters: boolean = false; // Allow only alphabetic input
  @Input() allowOnlyAlphanumeric: boolean = false; // Allow only alphanumeric input
  @Input() allowOnlyUppercase: boolean = false; // Convert input to uppercase
  @Input() allowOnlyLowercase: boolean = false; // Convert input to lowercase
  @Input() readonly: boolean = false;
  @Input() tabindex: number = 0;
  @Input() autocomplete: string = "off";
  @Input() spellcheck: boolean = false;
  @Input() inputStyle: any = {};
  @Input() inputStyleClass: string = "";
  @Input() panelStyle: any = {};
  @Input() panelStyleClass: string = "";
  @Input() styleClass: string = "";
  @Input() style: any = {};
  @Input() tooltip: string = "";
  @Input() tooltipPosition: 'above' | 'below' | 'left' | 'right' | 'dynamic' = 'dynamic';
  @Input() tooltipEvent: string = "hover";
  @Input() tooltipStyleClass: string = "";
  @Input() tooltipZIndex: string = "auto";
  @Input() tooltipEscape: boolean = true;
  @Input() tooltipAppendTo: string = "body";
  @Input() tooltipPositionStyle: string = "absolute";
  @Input() tooltipTheme: 'light' | 'dark' = 'dark';
  @Input() maxHeight: string = "200px"; // Maximum height for chips container
  @Input() showMoreChipsText: string = "+{count} more";
  @Input() showLessChipsText: string = "Show less";
  @Input() errorIcon: string = "info_filled_circle";
  @Input() showFavoriteToggle: boolean = false;
  @Input() showBadge: boolean = false;
  @Input() showActionSection: boolean = false;
  @Input() actionSectionPosition: 'top' | 'bottom' = 'bottom';
  @Input() actionSectionAlignment: 'left' | 'center' | 'right' = 'right';
  @Input() actionButtons: Array<{label: string, icon?: string, action: string, disabled?: boolean, variant?: 'primary' | 'secondary' | 'danger' | 'success'}> = [];
  @Input() openOnClick: boolean = false; // Whether to open options panel when clicking input
  @Input() loading: boolean = false;  // Whether to show loading indicator  

  // Output Events
  @Output() selectedChange = new EventEmitter<any>();
  @Output() search = new EventEmitter<AutocompleteCompleteEvent>();
  @Output() onSelect = new EventEmitter<AutocompleteSelectEvent>();
  @Output() onUnselect = new EventEmitter<AutocompleteUnselectEvent>();
  @Output() onFocus = new EventEmitter<void>();
  @Output() onBlur = new EventEmitter<void>();
  @Output() onDropdownClick = new EventEmitter<void>();
  @Output() onClear = new EventEmitter<void>();
  @Output() onLazyLoad = new EventEmitter<AutocompleteLazyLoadEvent>();
  @Output() onShow = new EventEmitter<void>();
  @Output() onHide = new EventEmitter<void>();
  @Output() onKeyUpEvent = new EventEmitter<KeyboardEvent>();
  @Output() onKeyDownEvent = new EventEmitter<KeyboardEvent>();
  @Output() onInputEvent = new EventEmitter<Event>();
  @Output() onChangeEvent = new EventEmitter<Event>();
  @Output() onComplete = new EventEmitter<AutocompleteCompleteEvent>();
  @Output() favoriteToggle = new EventEmitter<{option: AutocompleteOption, isFavorite: boolean}>();
  @Output() actionButtonClick = new EventEmitter<{action: string, event: Event}>();
  @Output() onScrollEnd = new EventEmitter<void>();
  @Output() leadingIconClick = new EventEmitter<MouseEvent>();
  @Output() trailingIconClick = new EventEmitter<MouseEvent>();


  // View Children
  @ViewChild('containerRef') containerRef!: ElementRef;
  @ViewChild('inputRef') inputRef!: ElementRef;
  @ViewChild('optionsRef') optionsRef!: ElementRef;
  @ViewChildren('optionRefs') optionRefs!: QueryList<ElementRef>;
  @ViewChild('chipsContainerRef') chipsContainerRef!: ElementRef;
  @ViewChild('dropdownSearchRef') dropdownSearchRef!: ElementRef;
  @ViewChild('chipMeasureRef') chipMeasureRef!: ElementRef;


  // Component State
  showOptions: boolean = false;
  query: string = "";
  filteredOptions: Array<AutocompleteOption> = [];
  filteredGroups: Array<AutocompleteGroup> = [];
  groupedOptions: Array<AutocompleteGroup> = [];
  selectedOptions: Array<AutocompleteOption> = [];
  currentPage: number = 1;
  totalItems: number = 0;
  searchTerm$ = new Subject<string>();
  isApiMode: boolean = false;
  highlightedIndex: number = -1;
  isFocused: boolean = false;
  isFloating: boolean = false;
  zIndex: number = 1000;
  showAllChips: boolean = false;
  visibleChipsCount: number = 0;

  // --- Private Properties ---
  private readonly destroy$ = new Subject<void>();
  private isDropdownClick: boolean = false;
  private ignoreBlur: boolean = false;
  private isClearing: boolean = false;
  private readonly previousChipCount: number = 0;
  private readonly previousContainerWidth: number = 0;

  // Control Value Accessor
  private onChangeCallback: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    private readonly autocompleteService: NiAutocompleteService,
    private readonly cdr: ChangeDetectorRef
  ) {
    // Setup API search if apiUrl is provided
    if (this.apiUrl) {
      this.autocompleteService.setupApiSearch(
        this.apiUrl,
        this.delay,
        this.minLength,
        this.apiParams,
        this.pageSize
      ).pipe(takeUntil(this.destroy$))
      .subscribe(({ results, totalItems }) => {
        this.filteredOptions = results;
        this.totalItems = totalItems;
        if (this.group) {
          this.groupedOptions = this.autocompleteService.groupOptions(results, this.groupField);
          this.filteredGroups = this.groupedOptions;
        }
        this.loading = false;
        this.cdr.detectChanges();
      });
    }
  }

  ngOnInit(): void {
    // Initialize API mode
    this.isApiMode = !!this.apiUrl;
    
    // Set up search term subscription for API mode
    if (this.isApiMode) {
      this.searchTerm$.pipe(
        takeUntil(this.destroy$)
      ).subscribe(term => {
        this.autocompleteService.emitSearchTerm(term);
      });
    } else {
      // For non-API mode, initialize with all options
      this.filteredOptions = [...this.options];
      if (this.group) {
        this.groupedOptions = this.autocompleteService.groupOptions(this.filteredOptions, this.groupField);
        this.filteredGroups = this.groupedOptions;
      }
    }
    
    // Set up z-index if autoZIndex is enabled
    if (this.autoZIndex) {
      this.zIndex = this.baseZIndex;
    }

    if (this.multiple && !Array.isArray(this.selected)) {
      this.selected = [];
    }

    if (this.selected === null || this.selected === undefined) {
      this.selected = this.multiple ? [] : null;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selected'] || changes['options']) {
      this.updateSelectedOptions();
    }
    
    if (changes['options']) {
      this.refreshFilteredOptions();
    }
    
    if (changes['apiUrl']) {
      this.isApiMode = !!this.apiUrl;
    }
    
    if (changes['autoZIndex'] && this.autoZIndex) {
      this.zIndex = this.baseZIndex;
    }
  }

  ngAfterViewInit(): void {
    this.recalculateVisibleChips();
    window.addEventListener('resize', this.onWindowResize);
  }

  ngAfterViewChecked(): void {
    // Component view has been checked
  }

  onFilteredOptionsChange(): void {
    // Filtered options have changed
  }

  ngOnDestroy(): void {
    // Clean up double-click timeout
    if (this.doubleClickTimeout) {
      clearTimeout(this.doubleClickTimeout);
      this.doubleClickTimeout = null;
    }
    this.destroy$.next();
    this.destroy$.complete();
    this.autocompleteService.destroy();
    window.removeEventListener('resize', this.onWindowResize);
  }

  onWindowResize = (): void => {
    this.recalculateVisibleChips();
  };



  recalculateVisibleChips(): void {
    // Use requestAnimationFrame for better performance
    requestAnimationFrame(() => {
      this.calculateVisibleChips();
    });
  }

  calculateVisibleChips(): void {
    if (!this.multiple || !this.chipMeasureRef || !this.chipsContainerRef) {
      this.visibleChipsCount = this.selectedOptions?.length ?? 0;
      return;
    }
  
    setTimeout(() => {
      const measureContainer = this.chipMeasureRef.nativeElement;
      const containerWidth = this.chipsContainerRef.nativeElement.offsetWidth;
  
      const chipElements = Array.from(
        measureContainer.querySelectorAll('[data-measure-chip]')
      ).filter((el): el is HTMLElement => el instanceof HTMLElement);

      const overflowIndicatorWidth = 40;
      let currentWidth = 0;
      let visibleCount = 0;

      for (const chip of chipElements) {
        const chipWidth = chip.offsetWidth;
        if (currentWidth + chipWidth > containerWidth - overflowIndicatorWidth) {
          break;
        }
        currentWidth += chipWidth;
        visibleCount++;
      }
  
      if (this.visibleChipsCount !== visibleCount) {
        this.visibleChipsCount = visibleCount;
        this.cdr.detectChanges();
      }
    }, 0);
  }
  

  // API Search Implementation
  private searchApi(term: string): Observable<AutocompleteOption[]> {
    if (!term || term.length < this.minLength) {
      return of([]);
    }

    this.loading = true;
    const event: AutocompleteCompleteEvent = { query: term };
    this.search.emit(event);
    this.onComplete.emit(event);

    if (!this.isApiMode) {
      return of(
        this.autocompleteService.filterLocalOptions(this.options, term, this.optionLabel)
      );
    }

    // For API mode, the service handles the search
    return of([]);
  }



  // Scroll Handler - Only for options panel
  onOptionsPanelScroll(event: Event): void {
    const element = event.target as HTMLElement;
    
    // Only handle scroll if this is the options panel
    if (element !== this.optionsRef?.nativeElement) {
      return;
    }
    
    if (this.isApiMode) {
      // Handle lazy loading for API mode
      const atBottom = element.scrollTop + element.clientHeight >= element.scrollHeight * this.scrollThreshold;
      if (atBottom && this.filteredOptions.length < this.totalItems && !this.loading) {
        this.loadMoreData();
        this.onScrollEnd.emit();
      }
    } else {
      // Handle regular scroll end for API pagination
      const atBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 10;
      if (atBottom && !this.loading) {
        this.onScrollEnd.emit();
      }
    }
  }

  loadMoreData(): void {
    this.loading = true;
    this.currentPage++;
    this.searchApi(this.query).subscribe(newOptions => {
        this.filteredOptions = [...this.filteredOptions, ...newOptions];
        if (this.group) {
          this.groupedOptions = this.autocompleteService.groupOptions(this.filteredOptions, this.groupField);
          this.filteredGroups = this.groupedOptions;
        }
      this.loading = false;
        this.cdr.detectChanges();
    });
  }

  // Control Value Accessor Methods
  writeValue(value: any): void {
    this.selected = value;
    this.updateSelectedOptions();
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // Filtering and Selection
  filterOptions(): void {
    if (!this.typeahead) return;
    
    // Apply regex validation to query for dropdown search
    this.validateQueryWithRegex();
    
    // If query length is less than or equal to minCharsToOpen, show all options (or empty if API mode)
    if (this.query.length <= this.minCharsToOpen) {
      if (this.isApiMode) {
        // For API mode, don't search if query is too short
        this.filteredOptions = [];
        this.loading = false;
        return;
      } else {
        // For local mode, show all options when query is too short
        this.filteredOptions = [...this.options];
        if (this.group) {
          this.groupedOptions = this.autocompleteService.groupOptions(this.filteredOptions, this.groupField);
          this.filteredGroups = this.groupedOptions;
        }
        this.loading = false;
        return;
      }
    }
    
    if (this.isApiMode) {
      this.loading = true;
      this.searchTerm$.next(this.query);
    } else {
      this.loading = true;
      setTimeout(() => {
        this.filteredOptions = this.autocompleteService.filterLocalOptions(
          this.options, 
          this.query, 
          this.optionLabel
        );
        
        if (this.group) {
          this.groupedOptions = this.autocompleteService.groupOptions(this.filteredOptions, this.groupField);
          this.filteredGroups = this.groupedOptions;
        }
        
        this.loading = false;
        
        // Auto-select if forceSelection and exact match found
        if (this.forceSelection && !this.multiple && this.query) {
          const exactMatch = this.filteredOptions.find(option => 
            String(option[this.optionLabel]) === this.query || 
            String(option[this.stateField]) === this.query
          );
          
          if (exactMatch) {
            this.selectSingleOption(exactMatch);
          }
        }
        
        // Ensure scrollbars are visible after filtering
        setTimeout(() => {
          this.ensureScrollbarVisibility();
        }, 50);
      }, this.delay);
    }
  }

  // Validate query with regex pattern
  private validateQueryWithRegex(): void {
    if (this.query) {
      const validatedQuery = this.validateInput(this.query);
      
      if (validatedQuery !== this.query) {
        this.query = validatedQuery;
        
        // Update the dropdown search input if it exists
        if (this.dropdownSearchRef?.nativeElement) {
          this.dropdownSearchRef.nativeElement.value = this.query;
        }
      }
    }
  }

  selectOption(option: AutocompleteOption): void {
    if (option.disabled) return;
  
    if (this.multiple) {
      this.toggleMultiSelectOption(option, false); // allow focus for multi-select
      // Keep the panel open and focus the input for multiple select
      setTimeout(() => {
        this.inputRef?.nativeElement.focus();
      }, 0);
    } else {
      this.selectSingleOption(option);
      // Only close panel for single select, but not if we're in a double-click sequence
      if (!this.isDoubleClicking) {
        this.hideOptions();
      }
    }
    this.onTouched();
    this.onSelect.emit({ originalEvent: new Event('select'), value: option });
    this.recalculateVisibleChips();
  }

  private isProgrammaticSelection = false; // Add this class variable

  private selectSingleOption(option: AutocompleteOption): void {
    this.isProgrammaticSelection = true; // Set flag when selecting programmatically
    this.selected = option;
    this.selectedOptions = [option];
    this.query = this.formatSelectedOption(option);
    this.showOptions = false;
    this.selectedChange.emit(option);
    this.onChangeCallback(option[this.valueField] || option);
    
    // Update tooltip content
    this.updateTooltip();
    
    // Reset flag after a short delay
    setTimeout(() => {
      this.isProgrammaticSelection = false;
    }, 200);
  }

  private toggleMultiSelectOption(option: AutocompleteOption, skipFocus: boolean = false): void {
    if (!Array.isArray(this.selected)) {
      this.selected = [];
    }
    const index = this.selected.findIndex((item: any) => 
      item === option[this.valueField] || 
      item === option[this.stateField] ||
      (typeof item === 'object' && item[this.valueField] === option[this.valueField])
    );
    const valueIndex = this.selectedOptions.findIndex(
      selected => selected[this.valueField] === option[this.valueField]
    );
    if (index === -1) {
      this.selected.unshift(option[this.valueField]);
      this.selectedOptions.unshift(option);
      this.onSelect.emit({ originalEvent: new Event('select'), value: option });
    } else {
      this.selected.splice(index, 1);
      const removed = this.selectedOptions.splice(valueIndex, 1);
      this.onUnselect.emit({ originalEvent: new Event('unselect'), value: removed[0] });
    }
    this.query = "";
    this.selectedChange.emit([...this.selectedOptions]);
    this.onChangeCallback(this.selectedOptions.map(opt => opt[this.valueField]));
    
    // Update tooltip content
    this.updateTooltip();
    
    if (!skipFocus) {
      this.focusInput();
    }
    // Recalculate visible chips after adding/removing
    this.recalculateVisibleChips();
  }

  // UI Interactions
  clearSelection(event?: Event): void {
    event?.stopPropagation();
    event?.preventDefault();
    
    // Set flag to prevent options panel from opening
    this.isClearing = true;
    
    this.selected = this.multiple ? [] : null;
    this.selectedOptions = [];
    this.query = "";
    this.filteredOptions = this.isApiMode ? [] : [...this.options];
    this.selectedChange.emit(this.selected);
    this.onChangeCallback(this.selected);
    this.onClear.emit();
    
    // Clear input value
    if (this.inputRef?.nativeElement) {
      this.inputRef.nativeElement.value = '';
    }
    
    // Update tooltip content
    this.updateTooltip();
    
    // Hide options first
    this.hideOptions();
    
    // Focus input after a delay to ensure options are hidden
    setTimeout(() => {
      this.inputRef?.nativeElement?.focus();
      this.inputRef?.nativeElement?.select();
      // Reset flag after focus
      this.isClearing = false;
    }, 100);
    
    this.visibleChipsCount = 0;
  }

  removeItem(option: any): void {
    if (!Array.isArray(this.selected)) return;
  
    this.ignoreBlur = true;
    
    const index = this.selected.findIndex((item: any) => 
      item === option[this.valueField] || 
      item === option[this.stateField] ||
      (typeof item === 'object' && item[this.valueField] === option[this.valueField])
    );
    
    const valueIndex = this.selectedOptions.findIndex(
      selected => selected[this.valueField] === option[this.valueField]
    );
  
    if (index !== -1) {
      this.selected.splice(index, 1);
      const removed = this.selectedOptions.splice(valueIndex, 1);
      this.selectedChange.emit([...this.selectedOptions]);
      this.onChangeCallback(this.selectedOptions.map(opt => opt[this.valueField]));
      this.onUnselect.emit({ originalEvent: new Event('unselect'), value: removed[0] });
    }
  
    // Update tooltip content
    this.updateTooltip();
  
    setTimeout(() => {
      this.ignoreBlur = false;
      this.focusInput();
      // Keep the dropdown open after removing a chip
      this.showOptions = true;
      // Recalculate visible chips after removal
      this.recalculateVisibleChips();
    }, 100);
  }

  toggleOptions(): void {
    if (this.disabled) return;
    
    this.showOptions = !this.showOptions;
    
    if (this.showOptions) {
      this.onShow.emit();
      // Reset highlighted index
      this.highlightedIndex = this.autoHighlight ? 0 : -1;
      this.ensureScrollbarVisibility();
      
      // Set z-index if autoZIndex is enabled
      if (this.autoZIndex) {
        this.zIndex = this.baseZIndex;
      }
      
      // Focus input when opening
      setTimeout(() => {
        this.inputRef?.nativeElement.focus();
      }, 50);
    } else {
      this.onHide.emit();
      this.highlightedIndex = -1;
    }
  }

  private lastHideCallTime = 0;
  private readonly hideCallDelay = 350; // milliseconds - slightly longer than double-click delay

  hideOptions(): void {
    // Prevent closing if we're in a double-click sequence
    if (this.isDoubleClicking) {
      return;
    }
    
    // Prevent rapid successive hide calls (which might indicate a double-click)
    const now = Date.now();
    if (this.lastHideCallTime && (now - this.lastHideCallTime < this.hideCallDelay)) {
      // This might be part of a double-click sequence, don't close
      this.isDoubleClicking = true;
      if (this.doubleClickTimeout) {
        clearTimeout(this.doubleClickTimeout);
      }
      this.doubleClickTimeout = setTimeout(() => {
        this.isDoubleClicking = false;
      }, 500);
      return;
    }
    this.lastHideCallTime = now;
    
    // Double-check that options panel still exists before closing
    if (!this.optionsRef?.nativeElement) {
      this.showOptions = false;
      this.onHide.emit();
      this.highlightedIndex = -1;
      return;
    }
    this.showOptions = false;
    this.onHide.emit();
    this.highlightedIndex = -1;

    // Always clear focus state when dropdown closes
    this.isFocused = false;

    // Force change detection to update container classes
    this.cdr.detectChanges();
  }

  // Dropdown Mode
  onDropdownButtonClick(): void {
    this.isDropdownClick = true;
    if (this.dropdownMode === 'blank') {
      this.query = '';
      this.searchTerm$.next('');
    }
    this.toggleOptions();
  }

  // Force Selection
  validateForceSelection(): void {
    if (!this.forceSelection || !this.query) return;

    const isValid = this.filteredOptions.some(option => 
      option[this.optionLabel] === this.query || option[this.stateField] === this.query
    );

    if (!isValid) {
      this.query = '';
      this.selected = this.multiple ? [] : null;
      this.onChangeCallback(this.selected);
      
      // Show error feedback
      this.hasError = true;
      setTimeout(() => {
        this.hasError = false;
      }, 2000);
    }
  }

  // Keyboard Navigation
  handleKeyDown(event: KeyboardEvent): void {
    if (this.disabled) return;
  
    if (this.handleInputElementKeyDown(event)) {
      return;
    }
  
    this.onKeyDownEvent.emit(event);
    this.handleNavigationKeys(event);
  }

  private handleInputElementKeyDown(event: KeyboardEvent): boolean {
    const target = event.target as HTMLElement;
    const isInputElement = target && (target.tagName === 'INPUT' || target === this.inputRef?.nativeElement);
    
    if (!isInputElement) {
      return false;
    }

    if (this.handleSpaceKey(event, target)) {
      return true;
    }

    const isNavigationKey = ['Enter', 'ArrowDown', 'ArrowUp', 'Escape', 'Tab'].includes(event.key);
    if (!isNavigationKey) {
      event.stopPropagation();
      return true;
    }

    return false;
  }

  private handleSpaceKey(event: KeyboardEvent, target: HTMLElement): boolean {
    if (event.key !== ' ' && event.key !== 'Spacebar') {
      return false;
    }

    const input = target as HTMLInputElement;
    const cursorPosition = input.selectionStart ?? 0;
    const textBeforeCursor = input.value.substring(0, cursorPosition).trim();
    
    if (cursorPosition === 0 || textBeforeCursor.length === 0) {
      event.preventDefault();
      event.stopPropagation();
      return true;
    }
    
    event.stopPropagation();
    return true;
  }

  private handleNavigationKeys(event: KeyboardEvent): void {
    switch (event.key) {
      case 'Enter':
        this.handleEnterKey(event);
        break;
      case 'ArrowDown':
        this.handleArrowDownKey(event);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.highlightPrevious();
        break;
      case 'Escape':
        event.preventDefault();
        this.hideOptions();
        break;
      case 'Tab':
        this.hideOptions();
        break;
    }
  }

  private handleEnterKey(event: KeyboardEvent): void {
    event.preventDefault();
    if (this.showOptions && this.highlightedIndex >= 0) {
      this.selectOption(this.filteredOptions[this.highlightedIndex]);
      if (this.multiple) {
        this.showOptions = true;
      }
    } else if (!this.multiple) {
      this.showOptions = true;
    }
  }

  private handleArrowDownKey(event: KeyboardEvent): void {
    event.preventDefault();
    if (!this.showOptions) {
      this.showOptions = true;
    } else {
      this.highlightNext();
    }
  }

  handleOptionKeyDown(event: KeyboardEvent, option: AutocompleteOption, index: number): void {
    const optionArray = this.optionRefs.toArray();
    
    switch (event.key) {
      case 'Enter':
        event.preventDefault();
        this.selectOption(option);
        setTimeout(() => this.multiple ? this.focusInput() : this.focusContainer(), 0);
        break;
      case 'ArrowDown':
        event.preventDefault();
        optionArray[(index + 1) % optionArray.length].nativeElement.focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        optionArray[(index - 1 + optionArray.length) % optionArray.length].nativeElement.focus();
        break;
      case 'Escape':
        event.preventDefault();
        this.hideOptions();
        setTimeout(() => this.multiple ? this.focusInput() : this.focusContainer(), 0);
        break;
      case 'Tab':
        this.hideOptions();
        break;
    }
  }

  // Highlight Management
  highlightNext(): void {
    if (this.highlightedIndex < this.filteredOptions.length - 1) {
      this.highlightedIndex++;
    } else {
      this.highlightedIndex = 0;
    }
    this.scrollToHighlighted();
  }

  highlightPrevious(): void {
    if (this.highlightedIndex > 0) {
      this.highlightedIndex--;
    } else {
      this.highlightedIndex = this.filteredOptions.length - 1;
    }
    this.scrollToHighlighted();
  }

  highlightFirst(): void {
    this.highlightedIndex = 0;
    this.scrollToHighlighted();
  }

  highlightLast(): void {
    this.highlightedIndex = this.filteredOptions.length - 1;
    this.scrollToHighlighted();
  }

  // Scroll to highlighted option
  scrollToHighlighted(): void {
    if (this.highlightedIndex >= 0) {
      const optionsElement = this.optionsRef?.nativeElement;
      if (!optionsElement) return;
      
      let highlightedElement: HTMLElement | null = null;
      
      // Try to find the highlighted element in regular options first
      highlightedElement = optionsElement.querySelector(`#option-${this.highlightedIndex}`);
      
      // If not found in regular options, try grouped options
      if (!highlightedElement && this.group) {
        highlightedElement = optionsElement.querySelector(`#option-${this.highlightedIndex}`);
      }
      
      if (highlightedElement) {
        // Ensure the options container has proper scroll settings
        if (optionsElement.scrollHeight > optionsElement.clientHeight) {
          optionsElement.style.overflowY = 'auto';
        }
        
        highlightedElement.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }

  // Focus Management
  focusInput(): void {
    setTimeout(() => {
      if (this.inputRef?.nativeElement) {
        this.inputRef.nativeElement.focus();
        this.inputRef.nativeElement.select();
      }
    }, 0);
    this.onFocus.emit();
  }

  focusContainer(): void {
    setTimeout(() => {
      if (this.containerRef?.nativeElement) {
        this.containerRef.nativeElement.focus();
      }
    }, 0);
  }

  onInputFocus(): void {
    // Don't show options panel when clearing
    if (this.isClearing) {
      this.isFocused = true;
      this.isFloating = true;
      this.onFocus.emit();
      this.cdr.detectChanges();
      return;
    }
    
    this.isFocused = true;
    this.isFloating = true;
    if (!this.multiple && !this.query && this.getDisplayValue()) {
      this.query = this.getDisplayValue();
    }
    this.onFocus.emit();
    
    // Don't open panel on initial focus - only open when typing more than minCharsToOpen characters
    // Panel will open automatically in onInputChange when query length > minCharsToOpen
    
    // Force change detection to update container classes
    this.cdr.detectChanges();
  }

  onInputBlur(): void {
    if (this.ignoreBlur || this.isProgrammaticSelection) return;

    setTimeout(() => {
      // Always clear focus when blur happens
      this.isFocused = false;

      // Only hide options if we're not in multiple select mode or if the dropdown wasn't clicked
      if (!this.isDropdownClick && !this.ignoreBlur && !this.multiple) {
        this.hideOptions();
      }
      this.isDropdownClick = false;
      this.onTouched();
      this.onBlur.emit();

      // Force change detection to update container classes
      this.cdr.detectChanges();
    }, 200);
  }

  onInputChange(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    let value = inputElement.value;
    
    // Apply comprehensive input validation
    value = this.validateInput(value);
    
    // Remove leading spaces - don't allow spaces at the beginning
    value = value.replace(/^\s+/, '');
    
    // Allow only one space - replace multiple consecutive spaces with a single space
    value = value.replace(/\s+/g, ' ');
    
    // Update input value if it changed
    if (value !== inputElement.value) {
      const cursorPosition = inputElement.selectionStart ?? 0;
      inputElement.value = value;
      // Restore cursor position after value update
      setTimeout(() => {
        const newPosition = Math.min(cursorPosition, value.length);
        inputElement.setSelectionRange(newPosition, newPosition);
      }, 0);
    }
    
    // Apply maxLength validation
    if (this.maxLength && value.length > this.maxLength) {
      value = value.slice(0, this.maxLength);
      inputElement.value = value;
    }
    
    this.query = value;
    
    // Trigger change detection to update input value binding (needed for OnPush strategy)
    this.cdr.markForCheck();
    
    // Show options when typing only if query length is greater than minCharsToOpen (unless disabled)
    // But keep panel open if it was already open from initial click
    if (!this.showOptions && !this.disabled && this.query.length > this.minCharsToOpen) {
      this.showOptions = true;
      this.onShow.emit();
    }
    // Note: We don't close the panel when query <= minCharsToOpen to allow initial click to keep it open
    
    // Filter options based on input (filtering will handle showing/hiding results based on query length)
    this.filterOptions();
    
    // When the query is cleared, close the panel and reset filtered options
    if (!this.query.trim()) {
      this.showOptions = false;
      this.highlightedIndex = -1;
      this.filteredOptions = this.isApiMode ? [] : [...this.options];
      if (this.group && !this.isApiMode) {
        this.groupedOptions = this.autocompleteService.groupOptions(this.filteredOptions, this.groupField);
        this.filteredGroups = this.groupedOptions;
      }
    }
    
    // Emit events
    this.onInputEvent.emit(event);
    this.onChangeEvent.emit(event);
    
    // Reset highlighted index when query changes
    this.highlightedIndex = this.autoHighlight ? 0 : -1;
    
    // For single select, clear the selected value when typing
    if (!this.multiple && this.query && this.selected) {
      this.selected = null;
      this.onChangeCallback(null);
      this.selectedChange.emit(null);
    }
  }

  // Comprehensive input validation method
  private validateInput(value: string): string {
    return this.autocompleteService.validateInput(value, {
      inputPattern: this.inputPattern,
      allowOnlyNumbers: this.allowOnlyNumbers,
      allowOnlyLetters: this.allowOnlyLetters,
      allowOnlyAlphanumeric: this.allowOnlyAlphanumeric,
      allowOnlyUppercase: this.allowOnlyUppercase,
      allowOnlyLowercase: this.allowOnlyLowercase,
      inputType: this.inputType
    });
  }

  onKeyUp(event: KeyboardEvent): void {
    this.onKeyUpEvent.emit(event);
  }

  // Utility Methods
  getSelected(): Array<AutocompleteOption> {
    if (this.multiple) {
      return Array.isArray(this.selected) ? this.selected as AutocompleteOption[] : [];
    }
    return this.selected ? [this.selected as AutocompleteOption] : [];
  }

  getDisplayValue(): string {
    if (this.multiple) {
      return this.selectedOptions.map(opt => this.formatSelectedOption(opt)).join(', ');
    }

    const resolvedOption = this.resolveSingleSelectedOption();

    if (resolvedOption) {
      return this.formatSelectedOption(resolvedOption);
    }

    if (this.selected === null || this.selected === undefined) {
      return '';
    }

    // Handle object stringification properly
    if (typeof this.selected === 'object') {
      const resolvedOption = this.resolveSingleSelectedOption();
      return resolvedOption ? this.formatSelectedOption(resolvedOption) : '';
    }

    return String(this.selected);
  }

  private resolveSingleSelectedOption(): AutocompleteOption | null {
    if (this.selectedOptions.length > 0) {
      return this.selectedOptions[0];
    }

    if (this.selected && !Array.isArray(this.selected) && typeof this.selected === 'object') {
      return this.selected;
    }

    if (this.selected !== null && this.selected !== undefined) {
      const matchingOption = this.options.find(opt =>
        opt[this.valueField] === this.selected ||
        opt[this.stateField] === this.selected
      );

      if (matchingOption) {
        return matchingOption;
      }
    }

    return null;
  }

  private formatSelectedOption(option: AutocompleteOption | null): string {
    if (!option) {
      return '';
    }

    const label = this.getOptionLabel(option);

    if (this.showSubtextInSelection && option.subtext) {
      const separator = this.selectedSubtextSeparator ?? '';
      return `${label}${separator}${option.subtext}`;
    }

    return label;
  }

  getInputValue(): string {
    if (this.multiple) {
      return '';
    }

    if (this.isFocused || this.showOptions || (this.query && this.query.length > 0)) {
      return this.query || '';
    }

    const displayValue = this.getDisplayValue();
    return displayValue || '';
  }

  getOptionLabel(option: AutocompleteOption): string {
    return this.autocompleteService.getOptionLabel(option, this.optionLabel);
  }

  isOptionSelected(option: AutocompleteOption): boolean {
    return this.autocompleteService.isOptionSelected(
      option, 
      this.selected, 
      this.multiple, 
      this.valueField
    );
  }

  trackByValue(index: number, item: any): any {
    if (typeof item === 'object' && item !== null) {
      return item[this.valueField] || item[this.stateField] || index;
    }
    return item || index;
  }

  // Helper method to check if an element is inside the options panel
  private isElementInsideOptionsPanel(element: any): boolean {
    if (!this.optionsRef?.nativeElement || !element) {
      return false;
    }
    
    // Direct contains check
    if (this.optionsRef.nativeElement.contains(element)) {
      return true;
    }
    
    // Traverse up the DOM tree to check parent elements
    let current: any = element;
    while (current && current !== document.body && current !== document.documentElement) {
      if (current === this.optionsRef.nativeElement) {
        return true;
      }
      current = current.parentNode || current.host; // host for shadow DOM
    }
    
    return false;
  }

  // Click Outside Handling
  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event): void {
    // If options panel is not open, don't do anything
    if (!this.showOptions || !this.optionsRef?.nativeElement) {
      return;
    }
    
    const target = event.target as Node;
    
    // Use composedPath for shadow DOM compatibility
    const path = event.composedPath ? event.composedPath() : [];
    
    // Check if click is inside options panel using multiple methods
    let isInsideOptions = this.isElementInsideOptionsPanel(target);
    
    // Also check all elements in the event path
    if (!isInsideOptions && path.length > 0) {
      isInsideOptions = path.some((node: any) => this.isElementInsideOptionsPanel(node));
    }
    
    // If inside options panel, NEVER close - return immediately
    if (isInsideOptions) {
      return;
    }
    
    // Check if click is inside container
    const isInsideContainer = this.containerRef?.nativeElement && (
      this.containerRef.nativeElement.contains(target) || 
      path.some((node: any) => node === this.containerRef?.nativeElement)
    );
    
    // Only close if clicking outside both container and options panel
    if (!isInsideContainer && !isInsideOptions) {
      this.hideOptions();
      // Clear focus state when clicking outside
      this.isFocused = false;
      this.cdr.detectChanges();
    }
  }

  // Catch mousedown events to prevent clicks inside options panel from closing
  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: Event): void {
    // If options panel is not open, don't do anything
    if (!this.showOptions || !this.optionsRef?.nativeElement) {
      return;
    }
    
    const target = event.target as Node;
    const path = event.composedPath ? event.composedPath() : [];
    
    // Check if mousedown is inside options panel
    const isInsideOptions = this.optionsRef.nativeElement.contains(target) || 
      path.some((node: any) => {
        // Check if node is a valid Node instance before calling contains
        if (!node || !(node instanceof Node)) {
          return false;
        }
        return node === this.optionsRef?.nativeElement || 
               this.optionsRef.nativeElement.contains(node);
      });
    
    // If inside options panel, stop propagation to prevent document click handler
    if (isInsideOptions) {
      event.stopPropagation();
    }
  }

  private lastMouseDownTime = 0;
  private isDoubleClicking = false;
  private doubleClickTimeout: any;

  // Prevent closing dropdown when clicking inside options panel (including padding)
  onOptionsPanelClick(event: MouseEvent): void {
    event.stopPropagation();
    event.stopImmediatePropagation();
    
    // Detect if this might be part of a double-click sequence
    const now = Date.now();
    if (this.lastMouseDownTime && (now - this.lastMouseDownTime < 300)) {
      // This is likely a double-click sequence
      this.isDoubleClicking = true;
      if (this.doubleClickTimeout) {
        clearTimeout(this.doubleClickTimeout);
      }
      this.doubleClickTimeout = setTimeout(() => {
        this.isDoubleClicking = false;
      }, 500);
    }
  }

  // Prevent closing dropdown when mousedown occurs inside options panel (including padding)
  onOptionsPanelMouseDown(event: MouseEvent): void {
    event.stopPropagation();
    event.stopImmediatePropagation();
    
    // Track mousedown time to detect double-clicks
    const now = Date.now();
    if (this.lastMouseDownTime && (now - this.lastMouseDownTime < 300)) {
      // This is likely a double-click sequence - set flag immediately
      this.isDoubleClicking = true;
      if (this.doubleClickTimeout) {
        clearTimeout(this.doubleClickTimeout);
      }
      this.doubleClickTimeout = setTimeout(() => {
        this.isDoubleClicking = false;
      }, 500);
    }
    this.lastMouseDownTime = now;
  }

  // Prevent closing dropdown when double-clicking inside options panel
  onOptionsPanelDoubleClick(event: MouseEvent): void {
    event.stopPropagation();
    event.stopImmediatePropagation();
    // Set flag to prevent hideOptions from closing the panel
    this.isDoubleClicking = true;
    if (this.doubleClickTimeout) {
      clearTimeout(this.doubleClickTimeout);
    }
    this.doubleClickTimeout = setTimeout(() => {
      this.isDoubleClicking = false;
    }, 500);
  }

  // Prevent closing dropdown when keyboard events occur inside options panel
  onOptionsPanelKeyDown(event: KeyboardEvent): void {
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  // Prevent closing dropdown when keyboard up events occur inside options panel
  onOptionsPanelKeyUp(event: KeyboardEvent): void {
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  // Prevent closing dropdown when keyboard press events occur inside options panel
  onOptionsPanelKeyPress(event: KeyboardEvent): void {
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  // Size Classes
  getSizeClasses(): string {
    switch (this.size) {
      case 'small':
        return 'text-sm px-2 py-1 min-h-8 h-8 rounded-md p-2';
      case 'large':
        return 'text-lg px-4 py-3 min-h-12 h-12 rounded-lg p-4';
      default:
        return 'text-base px-3 py-2 min-h-10 p-2';
    }
  }

  // Variant Classes
  getVariantClasses(): string {
    return this.variant === 'filled' ? 'bg-secondary-50' : 'bg-white';
  }

  // Fluid Classes
  getFluidClasses(): string {
    return this.fluid ? 'w-full' : 'w-auto';
  }

  // Error Classes
  getErrorClasses(): string {
    return this.hasError ? 'ring-2 ring-danger-500' : '';
  }

  // Border Classes
  getBorderClasses(): string {
    if (!this.border) {
      return 'border-0';
    }
    
    let borderClasses = 'border border-secondary-300';
    
    if (this.hasError) {
      borderClasses = 'border border-danger-500';
    } else if (this.isFocused) {
      borderClasses = 'border border-primary-500';
    }
    
    return borderClasses;
  }

  // Focus Classes
  getFocusClasses(): string {
    return this.isFocused ? 'ring-2 ring-primary-500' : '';
  }

  // Disabled Classes
  getDisabledClasses(): string {
    return this.disabled ? 'bg-secondary-100 cursor-not-allowed opacity-50' : '';
  }

  // Accessibility
  getAriaLabel(): string {
    return this.ariaLabel || this.label || '';
  }

  getAriaDescribedBy(): string {
    return this.ariaDescribedBy || '';
  }

  getAriaLabelledBy(): string {
    return this.ariaLabelledBy || '';
  }

  getAriaControls(): string {
    return this.showOptions ? 'autocomplete-options' : '';
  }

  getAriaExpanded(): string {
    return this.showOptions ? 'true' : 'false';
  }

  getAriaAutoComplete(): string {
    return this.multiple ? 'list' : 'both';
  }

  getAriaHasPopup(): string {
    return 'listbox';
  }

  getAriaActiveDescendant(): string {
    return this.highlightedIndex >= 0 ? `option-${this.highlightedIndex}` : '';
  }

  // Chip overflow handling
  getVisibleChips(): AutocompleteOption[] {    
    return this.autocompleteService.getVisibleChips(this.selectedOptions, this.visibleChipsCount);
  }

  getHiddenChipsCount(): number {
    return this.autocompleteService.getHiddenChipsCount(this.selectedOptions, this.visibleChipsCount);
  }

  getMoreChipsText(): string {
    const count = this.getHiddenChipsCount();
    return this.showMoreChipsText.replace('{count}', count.toString());
  }

  toggleChipsVisibility(): void {
    this.showAllChips = !this.showAllChips;
    // Ensure input gets focus after toggle
    setTimeout(() => {
      this.inputRef?.nativeElement?.focus();
    }, 100);
  }

  shouldShowToggleButton(): boolean {
    return this.multiple && this.showAllChips && this.selectedOptions.length > this.visibleChipsCount;
  }

  shouldShowScrollbar(): boolean {
    // For chips container in multiple selection
    if (this.multiple && this.selectedOptions.length > 0) {
      return this.showAllChips || this.visibleChipsCount === 0;
    }
    
    // For options dropdown
    if (this.showOptions) {
      const element = this.optionsRef?.nativeElement;
      return element ? element.scrollHeight > element.clientHeight : false;
    }
    
    return false;
  }

  ensureScrollbarVisibility(): void {
    if (this.showOptions) {
      const element = this.optionsRef?.nativeElement;
      if (element && element.scrollHeight > element.clientHeight) {
        element.style.overflowY = 'auto';
        element.style.overflowX = 'hidden';
      }
    }
  }



  showAllChipsAndDropdown() {
    this.showAllChips = true;
    this.showOptions = true;
    setTimeout(() => this.calculateVisibleChips(), 100);
  }

  // Handle count chip click
  handleCountChipClick(event: Event): void {
    if (event instanceof KeyboardEvent && event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    if (event instanceof KeyboardEvent) {
      event.preventDefault();
    }
    this.showOptions = true;
    this.isFocused = true;
    this.cdr.detectChanges();
    event.stopPropagation();
  }

  // Prevent blur from closing panel when interacting with dropdown search input
  onDropdownSearchInputMouseDown() {
    this.ignoreBlur = true;
    setTimeout(() => this.ignoreBlur = false, 150);
  }
  onDropdownSearchInputFocus() {
    this.ignoreBlur = true;
    setTimeout(() => {
      this.ignoreBlur = false;
    }, 150);
  }

  // Add to your component class
  handleChipsContainerClick(event: Event) {
    if (event instanceof KeyboardEvent && event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    if (event instanceof KeyboardEvent) {
      event.preventDefault();
    }
    // Prevent blur when clicking inside chips container
    this.ignoreBlur = true;
    setTimeout(() => this.ignoreBlur = false, 100);
    
    // Set focus state
    this.isFocused = true;
    
    // Open panel on click if openOnClick is enabled
    if (this.openOnClick) {
      if (!this.showOptions) {
        this.showOptions = true;
        this.onShow.emit();
        this.highlightedIndex = this.autoHighlight ? 0 : -1;
        this.ensureScrollbarVisibility();
      }
    }
    
    // Focus the input for typing
    setTimeout(() => {
      this.inputRef?.nativeElement?.focus();
    }, 0);
    
    // Force change detection to update container classes
    this.cdr.detectChanges();
    
    event.stopPropagation();
  }

  handleDropdownClick(event: MouseEvent) {
    this.isDropdownClick = true;
    this.toggleOptions();
    event.stopPropagation();
    event.preventDefault();
  }

  handleLeadingIconClick(event: Event): void {
    if (event instanceof KeyboardEvent && event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    if (event instanceof KeyboardEvent) {
      event.preventDefault();
    }
    if (this.disabled || !this.shouldShowLeadingIconSingle() && !this.shouldShowLeadingIconMultiple()) {
      return;
    }
    event.stopPropagation();
    this.ignoreBlur = true;
    if (event instanceof MouseEvent) {
      this.leadingIconClick.emit(event);
    }
    // Don't open panel on leading icon click - only open when typing more than minCharsToOpen characters
    // Just focus the input for typing
    if (!this.multiple) {
      this.focusInput();
    } else {
      setTimeout(() => {
        this.inputRef?.nativeElement.focus();
      }, 0);
    }
    setTimeout(() => {
      this.ignoreBlur = false;
    }, 150);
  }

  handleTrailingIconClick(event: Event): void {
    if (event instanceof KeyboardEvent && event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    if (event instanceof KeyboardEvent) {
      event.preventDefault();
    }
    if (this.disabled || !this.shouldShowTrailingIcon()) {
      return;
    }
    event.stopPropagation();
    this.ignoreBlur = true;
    if (event instanceof MouseEvent) {
      this.trailingIconClick.emit(event);
    }
    setTimeout(() => {
      this.ignoreBlur = false;
    }, 150);
  }

  onInputClick(event: MouseEvent) {
    if (this.disabled) return;
    
    // Open panel on click if openOnClick is enabled
    if (this.openOnClick) {
      if (!this.showOptions) {
        this.showOptions = true;
        this.onShow.emit();
        this.highlightedIndex = this.autoHighlight ? 0 : -1;
        this.ensureScrollbarVisibility();
      }
    }
    
    // Ensure input gets focus for typing
    setTimeout(() => {
      this.inputRef?.nativeElement?.focus();
    }, 0);
    
    event.stopPropagation();
  }

  onProfileImageMouseDown(event: MouseEvent) {
    if (this.disabled) return;
    
    // Prevent blur from firing when clicking on profile image
    this.ignoreBlur = true;
    event.preventDefault();
  }

  onProfileImageClick(event: Event) {
    if (event instanceof KeyboardEvent && event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    if (this.disabled) return;
    
    // Stop event propagation to prevent multiple handlers
    event.stopPropagation();
    if (event instanceof KeyboardEvent) {
      event.preventDefault();
    } else {
      (event as MouseEvent).preventDefault();
    }
    
    // Don't open panel on profile image click - only open when typing more than minCharsToOpen characters
    // Just focus the input for typing
    this.ignoreBlur = true;
    setTimeout(() => {
      this.inputRef?.nativeElement?.focus();
      setTimeout(() => {
        this.ignoreBlur = false;
      }, 150);
    }, 0);
  }

  onInputWrapperClick(event: Event) {
    if (event instanceof KeyboardEvent && event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    if (event instanceof KeyboardEvent) {
      event.preventDefault();
    }
    if (this.disabled) return;
    
    // If click is on the wrapper but not on the input or profile image
    const target = event.target as HTMLElement;
    if (!target.closest('input') && !target.closest('.ni-single-profile-image')) {
      // Open panel on click if openOnClick is enabled
      if (this.openOnClick) {
        if (!this.showOptions) {
          this.showOptions = true;
          this.onShow.emit();
          this.highlightedIndex = this.autoHighlight ? 0 : -1;
          this.ensureScrollbarVisibility();
        }
      }
      
      // Focus the input for typing
      setTimeout(() => {
        this.inputRef?.nativeElement.focus();
      }, 0);
    }
  }

  handleChipClick(event: Event, option: AutocompleteOption) {
    if (event instanceof KeyboardEvent && event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    if (event instanceof KeyboardEvent) {
      event.preventDefault();
    }
    if (this.disabled) return;
    
    // Prevent the container click from firing
    event.stopPropagation();
    
    // Prevent blur when clicking on chip
    this.ignoreBlur = true;
    setTimeout(() => this.ignoreBlur = false, 100);
    
    // Set focus state
    this.isFocused = true;
    
    // Don't open panel on chip click - only open when typing more than minCharsToOpen characters
    // Focus the input for typing
    setTimeout(() => {
      this.inputRef?.nativeElement?.focus();
    }, 0);
    
    // Force change detection to update container classes
    this.cdr.detectChanges();
  }

  onInputMouseDown(event: MouseEvent) {
    if (this.disabled) return;
    
    this.ignoreBlur = true;
    
    setTimeout(() => {
      this.ignoreBlur = false;
    }, 200);
  }

  // Update selected options
  updateSelectedOptions(): void {
    if (this.selected === null || this.selected === undefined) {
      this.selectedOptions = [];
      return;
    }

    if (this.multiple) {
      const selectedValues = Array.isArray(this.selected) ? this.selected : [this.selected];
      this.selectedOptions = this.options.filter(option => 
        selectedValues.some((val: any) => {
          const optionValue = option[this.valueField];
          if (typeof val === 'object' && val !== null && val.hasOwnProperty(this.valueField)) {
            return val[this.valueField] === optionValue;
          }
          return val === optionValue;
        })
      );
    } else {
      const matchingOption = this.options.find(option => {
        const optionValue = option[this.valueField];
        if (typeof this.selected === 'object' && this.selected !== null && this.selected.hasOwnProperty(this.valueField)) {
          return (this.selected as Record<string, unknown>)[this.valueField] === optionValue;
        }
        return this.selected === optionValue;
      });
      this.selectedOptions = matchingOption ? [matchingOption] : [];
    }
    
    this.recalculateVisibleChips();
  }

  private refreshFilteredOptions(): void {
    const wasDropdownOpen = this.showOptions;
    const hadQuery = this.query && this.query.length > 0;
    
    if (this.isApiMode) {
      this.handleApiModeRefresh(wasDropdownOpen);
      return;
    }

    if (this.shouldUseTypeaheadFilter()) {
      this.handleTypeaheadRefresh(wasDropdownOpen);
      return;
    }

    this.handleNonTypeaheadRefresh(wasDropdownOpen, hadQuery as boolean);
  }

  private handleApiModeRefresh(wasDropdownOpen: boolean): void {
    if (!wasDropdownOpen || this.options.length === 0) {
      return;
    }
    
    this.filteredOptions = [...this.options];
    this.updateGroupedOptions();
    this.showOptions = true;
  }

  private handleTypeaheadRefresh(wasDropdownOpen: boolean): void {
    this.filterOptions();
    if (wasDropdownOpen && this.filteredOptions.length > 0) {
      this.showOptions = true;
    }
  }

  private handleNonTypeaheadRefresh(wasDropdownOpen: boolean, hadQuery: boolean): void {
    this.filteredOptions = [...this.options];
    this.updateGroupedOptions();
    
    if (wasDropdownOpen && this.filteredOptions.length > 0 && hadQuery) {
      this.showOptions = true;
    }
  }

  private shouldUseTypeaheadFilter(): boolean {
    return this.typeahead && this.query.length > this.minCharsToOpen;
  }

  private updateGroupedOptions(): void {
    if (!this.group) {
      return;
    }
    this.groupedOptions = this.autocompleteService.groupOptions(this.filteredOptions, this.groupField);
    this.filteredGroups = this.groupedOptions;
  }

  // Get dynamic tooltip content
  getTooltipContent(): string {
    if (this.tooltip) {
      return this.tooltip;
    }
    
    if (this.multiple) {
      if (this.selectedOptions.length > 0) {
        return `Selected: ${this.selectedOptions.map(opt => this.getOptionLabel(opt)).join(', ')}`;
      }
      return this.placeholder || 'Select options...';
    } else {
      if (this.selected) {
        return `Selected: ${this.getDisplayValue()}`;
      }
      return this.placeholder || 'Select an option...';
    }
  }

  // Update tooltip when selection changes
  private updateTooltip(): void {
    // Force change detection to update tooltip content
    this.cdr.detectChanges();
  }

  getFirstLetter(option: AutocompleteOption): string {
    const label = this.getOptionLabel(option);
    return label ? label.charAt(0).toUpperCase() : '?';
  }

  onImageError(event: Event, option: AutocompleteOption, imageType: 'profileImage' | 'trailingProfileImage'): void {
    // Mark the image as not loaded based on type
    if (imageType === 'profileImage') {
      option['imageLoaded'] = false;
    } else if (imageType === 'trailingProfileImage') {
      option['trailingImageLoaded'] = false;
    }
    this.cdr.detectChanges();
  }

  onFavoriteToggle(event: {option: AutocompleteOption, isFavorite: boolean}): void {
    // Update the option's isFavorite property
    event.option.isFavorite = event.isFavorite;
    
    // Emit the event to parent component
    this.favoriteToggle.emit(event);
  }

  getFavoriteOptions(): AutocompleteOption[] {
    return this.options.filter(option => option.isFavorite === true);
  }

  get favoriteFilteredOptions(): AutocompleteOption[] {
    return this.filteredOptions.filter(option => option.isFavorite === true);
  }

  get nonFavoriteFilteredOptions(): AutocompleteOption[] {
    return this.filteredOptions.filter(option => !option.isFavorite);
  }

  // Comprehensive styling classes
  getComprehensiveClasses(): string {
    const classes = [
      this.getSizeClasses(),
      this.getVariantClasses(),
      this.getFluidClasses(),
      this.getErrorClasses(),
      this.getBorderClasses(),
      this.getFocusClasses(),
      this.getDisabledClasses()
    ].filter(Boolean);
    
    return classes.join(' ');
  }

  getOptionsDropdownClasses(): string {
    const classes = ['ni-options-dropdown'];
    
    // Add classes based on features present
    if (this.showActionSection && this.actionButtons.length > 0) {
      classes.push('has-action-buttons');
    }
    
    if (this.showFavoriteToggle && this.favoriteFilteredOptions.length > 0) {
      classes.push('has-favorites');
    }
    
    return classes.join(' ');
  }

  getContainerClasses(): string {
    const classes = [];
    
    if (this.multiple) {
      classes.push('ni-multiple-container');
      if (this.isFocused) {
        classes.push('focused');
      }
      if (this.hasError) {
        classes.push('error');
      }
    } else {
      classes.push('ni-single-container');
    }
    
    return classes.join(' ');
  }

  onActionButtonClick(action: string, event: Event): void {
    event.stopPropagation();
    this.actionButtonClick.emit({ action, event });
  }

  toggleFavorite(option: AutocompleteOption, event: Event): void {
    event.stopPropagation();
    
    if (this.showFavoriteToggle) {
      option.isFavorite = !option.isFavorite;
      
      // Emit favorite toggle event
      this.favoriteToggle.emit({
        option,
        isFavorite: option.isFavorite
      });
      
      // Ensure proper scroll behavior after favorite toggle
      this.onFilteredOptionsChange();
    }
  }

  handlePlaceholderClick(event: Event): void {
    if (event instanceof KeyboardEvent && event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    if (event instanceof KeyboardEvent) {
      event.preventDefault();
    }
    this.isFocused = true;
    
    // Open panel on click if openOnClick is enabled
    if (this.openOnClick) {
      if (!this.showOptions) {
        this.showOptions = true;
        this.onShow.emit();
        this.highlightedIndex = this.autoHighlight ? 0 : -1;
        this.ensureScrollbarVisibility();
      }
    }
    
    // Focus the input for typing
    setTimeout(() => {
      this.inputRef?.nativeElement?.focus();
    }, 0);
    this.cdr.detectChanges();
    event.stopPropagation();
  }

  shouldShowLeadingIconSingle(): boolean {
    if (this.multiple || !this.leadingIcon) {
      return false;
    }
    const hasProfileImage = this.selectedOptions.length > 0 && !!this.selectedOptions[0]?.['profileImage'];
    return !hasProfileImage;
  }

  shouldShowLeadingIconMultiple(): boolean {
    return this.multiple && !!this.leadingIcon;
  }

  shouldShowLeadingIcon(): boolean {
    return this.shouldShowLeadingIconSingle() || this.shouldShowLeadingIconMultiple();
  }

  shouldShowTrailingIcon(): boolean {
    return !!this.trailingIcon;
  }
}